import type { AnnouncementModalContent } from '../components/Modals/Announcement/Announcement'
import { api } from '../lib/axios'
import { useCallback, useEffect, useState } from 'react'

type NextCampaignResponse = {
  campaign: (AnnouncementModalContent & {
    id: number
    featureId: number
    dayOfWeek: 'monday' | 'wednesday' | 'friday'
  }) | null
}

const SESSION_STORAGE_KEY = '@whatsmenu:campaign-seen'

export function useSessionModal(enabled: boolean, authToken?: string | null) {
  const [campaign, setCampaign] = useState<AnnouncementModalContent | null>(null)
  const [show, setShow] = useState(false)
  const [loading, setLoading] = useState(false)

  const markAsSeen = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, 'true')
    }

    setShow(false)
  }, [])

  useEffect(() => {
    if (!enabled) {
      setCampaign(null)
      setShow(false)
      setLoading(false)
      return
    }

    if (!authToken) {
      setCampaign(null)
      setShow(false)
      setLoading(false)
      return
    }

    if (typeof window === 'undefined') {
      return
    }

    if (window.sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true') {
      setCampaign(null)
      setShow(false)
      setLoading(false)
      return
    }

    let cancelled = false
    api.defaults.headers.common.Authorization = `Bearer ${authToken}`

    const loadCampaign = async () => {
      setLoading(true)

      try {
        const { data } = await api.get<NextCampaignResponse>('/dashboard/features/next-campaign')

        if (cancelled) return

        if (data.campaign) {
          setCampaign({
            badge: data.campaign.badge,
            title: data.campaign.title,
            body: data.campaign.body,
            imageUrl: data.campaign.imageUrl ?? null,
            ctaLabel: data.campaign.ctaLabel,
            ctaUrl: data.campaign.ctaUrl ?? null,
            learnMoreUrl: data.campaign.learnMoreUrl ?? null,
            learnMoreLabel: data.campaign.learnMoreLabel ?? undefined,
          })
          setShow(true)
        } else {
          setCampaign(null)
          setShow(false)
        }
      } catch {
        if (!cancelled) {
          setCampaign(null)
          setShow(false)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void loadCampaign()

    return () => {
      cancelled = true
    }
  }, [enabled, authToken])

  return {
    campaign,
    show,
    loading,
    markAsSeen,
  }
}
